import type { z } from 'zod';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import { useProjectStore } from '../../../store/projectStore';
import { useProxyStore } from '../../../store/proxy-store';
import {
  buildPortalToolErrorResult,
  type PortalToolHandlerResult,
} from './types';

export function parsePortalToolHandlerInput<T>({
  schema,
  input,
  toolName,
  toolCallId,
  batchId,
}: {
  schema: z.ZodType<T>;
  input: Record<string, unknown>;
  toolName: string;
  toolCallId: string;
  batchId: string;
}): { ok: true; data: T } | { ok: false; result: PortalToolHandlerResult } {
  const parsed = schema.safeParse(input);

  if (!parsed.success) {
    return {
      ok: false,
      result: buildPortalToolErrorResult({
        toolName,
        toolCallId,
        batchId,
        message: `Invalid input: ${parsed.error.message}`,
      }),
    };
  }

  return { ok: true, data: parsed.data };
}

export function isElectronApiAvailable(): boolean {
  return typeof window !== 'undefined' && Boolean(window.electronAPI);
}

export function getElectronApiUnavailableResult({
  toolName,
  toolCallId,
  batchId,
  message = 'Electron API is not available',
}: {
  toolName: string;
  toolCallId: string;
  batchId: string;
  message?: string;
}): PortalToolHandlerResult {
  return buildPortalToolErrorResult({
    toolName,
    toolCallId,
    batchId,
    message,
  });
}

export function resolvePortalTargetId({
  applicationId,
  targetId,
}: {
  applicationId?: string;
  targetId?: string;
}): string {
  const explicitTargetId = targetId?.trim() || applicationId?.trim();

  if (explicitTargetId) {
    return explicitTargetId;
  }

  const projectTargetId = useProjectStore.getState().targetId;
  const proxyTargetId = useProxyStore.getState().currentTargetId;

  return projectTargetId || proxyTargetId || 'standalone';
}

export function resolvePortalTargetScope({
  applicationId,
  targetId,
  targetType,
}: {
  applicationId?: string;
  targetId?: string;
  targetType?: TargetTypeType;
}): { targetId: string; targetType: TargetTypeType } {
  const resolvedTargetId = resolvePortalTargetId({ applicationId, targetId });
  const projectState = useProjectStore.getState();
  const proxyState = useProxyStore.getState();

  if (targetType) {
    return { targetId: resolvedTargetId, targetType };
  }

  if (resolvedTargetId === projectState.targetId) {
    return {
      targetId: resolvedTargetId,
      targetType: projectState.targetType,
    };
  }

  if (
    resolvedTargetId === proxyState.currentTargetId &&
    proxyState.currentTargetType
  ) {
    return {
      targetId: resolvedTargetId,
      targetType: proxyState.currentTargetType,
    };
  }

  return {
    targetId: resolvedTargetId,
    targetType: projectState.targetType,
  };
}

export function resolvePortalApplicationId({
  applicationId,
}: {
  applicationId: string;
}): string {
  return resolvePortalTargetId({ applicationId });
}

export function createPortalToolExecutionTracker() {
  const processedToolCalls = new Map<string, string | undefined>();

  return {
    registerExecution({
      toolCallId,
      chatId,
    }: {
      toolCallId: string;
      chatId?: string;
    }): 'execute' | 'duplicate' {
      if (processedToolCalls.has(toolCallId)) {
        return 'duplicate';
      }

      processedToolCalls.set(toolCallId, chatId);

      return 'execute';
    },
    clearChat(chatId: string): void {
      Array.from(processedToolCalls.entries())
        .filter(([, ownerChatId]) => ownerChatId === chatId)
        .forEach(([toolCallId]) => processedToolCalls.delete(toolCallId));
    },
    clear(): void {
      processedToolCalls.clear();
    },
  };
}

export type PortalToolExecutionTracker = ReturnType<
  typeof createPortalToolExecutionTracker
>;
